import { useMutation, useQueryClient } from '@tanstack/react-query';

import { roleService } from '@/services/role-service';
import type { Role } from '@/types/core/role';
import { pollUntilResourceNewerThan } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { roleMutationKeys, roleQueryKeys } from './query-keys';

/**
 * Hook to update a role.
 * Returns a react-query mutation that accepts partial role updates.
 * Waits until the read model has a newer _rev (or resource exists when backend doesn't send _rev yet).
 */
export function useUpdateRole(tenantId?: UUID, roleId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: roleMutationKeys.updateRole(tenantId, roleId),
    mutationFn: async (updates: Partial<Omit<Role, 'roleId'>>) => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!roleId) throw new Error('Missing role id');

      const cachedRole = qc.getQueryData<Role | null>(
        roleQueryKeys.detail(tenantId, roleId)
      );
      const minRev = cachedRole?._rev ?? -2;

      await roleService.updateRole(tenantId, roleId, updates);

      const updatedRole = await pollUntilResourceNewerThan(
        () => roleService.findRole(tenantId, roleId),
        minRev,
        { maxAttempts: 10, delayMs: 300 }
      );

      return updatedRole;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: roleQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });
      qc.invalidateQueries({
        queryKey: roleQueryKeys.detail(tenantId, roleId),
      });
    },
  });
}
